import { TheftReportAttachment } from './report-portal';
import { ReviewSubjectType } from './review';
import { ValuesOf } from './utils';

export type CaseTrackingBranch = {
  branch_id: number;
  branch_name: string;
};

export type CaseTrackingLocation = {
  location_id: number;
  location_name: string;

  has_recent_incidents: boolean;
  recent_incidents_count: number;

  // used in the frontend to group locations in the ui
  _group?: string;
};

export type CaseTrackingBranchAndLocation = CaseTrackingBranch & CaseTrackingLocation;

/** Case status representing different stages in the case lifecycle */
export enum CaseStatus {
  /** Case created from new incidents */
  New = 'New',
  /** Case viewed by branch users */
  Viewed = 'Viewed',
  /** Case viewed by clients (non-branch users) */
  ViewedByClient = 'ViewedByClient',
  /** Case marked as resolved by anyone */
  Resolved = 'Resolved',
  /** Case reopened by new incidents after being resolved */
  Reopened = 'Reopened',
  /** Case archived manually */
  Archived = 'Archived',
}

export type CaseMetadata = {
  cashier_id?: string;
  name?: string; // custom name for a case
};

export type Case = {
  id: string;
  location_id: number;
  location_name: string;
  location_timezone?: string;
  branch_id: number;
  branch_name: string;
  case_index: number;
  status: CaseStatus;
  created_at: Date;
  metadata: CaseMetadata;
};

export type CaseAccessLogDetail = {
  email: string;
  last_access: string;
};

export type AccessType = 'case' | 'guest_dashboard' | 'share' | 'report';

export type CaseWithIncidents = Case & {
  external_location_id?: string;
  external_branch_id?: string;
  first_incident_date: Date;
  most_recent_incident_date: Date;
  viewers: CaseAccessLogDetail[];
  theft_report_share_key: string;
  incidents: CaseIncident[];
  location_group_id?: string;
  incidents_count: number; // this is the total amount, which can differ from incidents.length because date filters are applied
};

export const REVIEW_OUTCOME_STATUS = {
  NEW: 'New',
  VIEWED: 'Viewed',
  ARCHIVED: 'Archived',
} as const;

export type ReviewOutcomeStatus = ValuesOf<typeof REVIEW_OUTCOME_STATUS>;

export type CaseIncidentSummary = {
  review_id?: string;
  review_outcome_id: string;
  incident_date?: Date;
  date_shared?: Date;
  status: ReviewOutcomeStatus;
  summary: string;
};

export type CaseIncident = CaseIncidentSummary & {
  review_subject: ReviewSubjectType;
  video_links: string[];
  attachments: TheftReportAttachment[];
  instance_num: number;
  is_self_service: boolean;
  is_hidden: boolean;
  use_kiosk_photo?: boolean;
  image_id?: string;
  item_value?: number;
  terminal_id?: string;
  cashier_id?: string;
  loss_represented?: number;
  custom_theft_type?: string;
  incident_types?: string[];
};

export type CaseIncidentTableEntry = Omit<CaseWithIncidents, 'first_incident_date' | 'most_recent_incident_date' | 'case_index'> &
  Omit<CaseIncident, 'date_shared' | 'status'> & {
    case_id: string;
    case_index: string;
    first_incident_date: Date;
    most_recent_incident_date: Date;
    date_shared: Date;
  };

export type ShareLogEntry = {
  sender: string;
  recipient?: string;
  sent_at: string;
};

export type CaseShareHistory = {
  case_id: string;
  share_logs: ShareLogEntry[];
};

export type ShareCasesRequest = {
  emails?: string[];
  case_ids: string[];
  to_guests?: boolean;
  cc_sender?: boolean;
  message?: string; // a custom message the sender can provide
};

export type GetCasesByLocationsRequest = {
  location_ids: number[];
  branch_ids: number[];
  location_group_ids: string[];
  incident_date_from?: Date;
  incident_date_to?: Date;
  mode: GetCasesMode;
};

export type GetCasesMode = 'default' | 'guest';
